import React, { FC, useState, useMemo, SyntheticEvent } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import isEmpty from 'lodash/isEmpty'
import Input from '@vtex/styleguide/lib/Input'
import { withMediaQuery } from '@/checkout-instore-legacy/utils/mediaQuery'

import Link from '@/checkout-instore-legacy/components/Commons/Elements/Link'
import SectionTitle from '@/checkout-instore-legacy/components/Commons/Elements/SectionTitle'
import { getPickupOnlyLogisticsInfo } from '@/checkout-instore-legacy/shipping/utils'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'

interface AddressFormProps {
  address: Address
  logisticsInfo: LogisticsInfo
  intl: IntlObject
  allowCollapsed: boolean
  mediaQuery: (options: {
    phone: boolean
    tablet: boolean
    desktop: boolean
  }) => boolean
  onAddressChange: (address: Address) => void
}

const shouldCollapseAddress = ({
  street,
  neighborhood,
  city,
  state,
}: {
  street?: string
  neighborhood?: string
  city?: string
  state?: string
}): boolean => {
  return !!(street && neighborhood && city && state)
}

type AddressStateKey =
  | 'street'
  | 'neighborhood'
  | 'city'
  | 'state'
  | 'number'
  | 'complement'
  | 'reference'

const AddressForm: FC<AddressFormProps> = ({
  intl,
  logisticsInfo,
  address,
  allowCollapsed,
  onAddressChange,
  mediaQuery,
}: AddressFormProps) => {
  const [isAddressCollapsed, setAddressCollapse] = useState(
    shouldCollapseAddress(address)
  )
  const [touched, setTouched] = useState({
    street: false,
    neighborhood: false,
    city: false,
    state: false,
    number: false,
    complement: false,
    reference: false,
  })
  const mobile = mediaQuery({
    phone: true,
    tablet: false,
    desktop: false,
  })
  const fieldSize = mobile ? 'regular' : 'large'

  const { labels, placeholders } = useMemo(
    () => ({
      labels: {
        street: intl.formatMessage({
          id: 'shippingAddressStreet',
        }),
        neighborhood: intl.formatMessage({
          id: 'shippingAddressNeighborhood',
        }),
        city: intl.formatMessage({
          id: 'shippingAddressCity',
        }),
        state: intl.formatMessage({
          id: 'shippingAddressState',
        }),
        number: intl.formatMessage({
          id: 'shippingAddressNumber',
        }),
        complement: intl.formatMessage({
          id: 'shippingAddressComplement',
        }),
        reference: intl.formatMessage({
          id: 'shippingAddressReference',
        }),
        receiverName: intl.formatMessage({
          id: 'shippingAddressReceiver',
        }),
      },
      placeholders: {
        complement: intl.formatMessage({
          id: 'shippingAddressComplementPlaceHolder',
        }),
      },
    }),
    [intl]
  )

  const isPickupOnlyLogisticsInfo = useMemo(() => {
    if (isEmpty(logisticsInfo)) {
      return false
    }
    const pickupOnlyLogisticsInfo = getPickupOnlyLogisticsInfo(logisticsInfo)
    return pickupOnlyLogisticsInfo.length === logisticsInfo.length
  }, [logisticsInfo])

  const handleToggleAddressCollapsed = (e: SyntheticEvent) => {
    e.preventDefault()
    setAddressCollapse(!isAddressCollapsed)
  }

  const onInputChange = (stateId: string, value: string) => {
    onAddressChange({ ...address, [stateId]: value })
  }

  const isComplementValid = (complement: string) => {
    const hasNotAllowedChar = /<|>|\?|\+|"|;|%/.test(complement)

    return !hasNotAllowedChar
  }

  const getLabel = (name: AddressStateKey) => {
    if (name === 'complement') {
      return (
        <div className="flex flex-auto">
          {labels[name]}
          {
            <div className="flex-auto c-muted-1 t-small-ns ml3 mt1 t-mini-s">
              {placeholders.complement}
            </div>
          }
        </div>
      )
    }
    return labels[name]
  }

  const renderInput = (
    name: AddressStateKey,
    autoFocus = false,
    type = 'text'
  ) => {
    const value = address[name]
    const label = getLabel(name)

    const getError = () => {
      if (name === 'complement') {
        return !isComplementValid(value || '')
      }
      return touched[name] === true && value === ''
    }

    const getErrorMessage = () => {
      if (!getError()) {
        return
      }
      const messageError =
        name === 'complement'
          ? intl.formatMessage({
              id: 'shippingAddressComplementNotAllowed',
            })
          : intl.formatMessage({
              id: 'shippingAddressErrorEmptyField',
            })

      return messageError
    }

    return (
      <div className="pb5">
        <Input
          id={`address-${name.toLowerCase()}-input`}
          value={value}
          label={label}
          type={type}
          autoFocus={autoFocus}
          onChange={(e: InputValueEvent) => onInputChange(name, e.target.value)}
          error={getError()}
          errorMessage={getErrorMessage()}
          size={fieldSize}
          onBlur={(e: InputValueEvent) => {
            if (!e.focusInCurrentTarget) {
              setTouched({ ...touched, [name]: true })
            }
          }}
        />
      </div>
    )
  }

  const { showAddressReference } = getInstoreConfig()

  const { street, neighborhood, city, state, number } = address

  return (
    <div>
      {isAddressCollapsed && allowCollapsed ? (
        <div className="f5 f5-ns black mb5 mb6-ns">
          <SectionTitle contentClassName="">
            {isPickupOnlyLogisticsInfo ? (
              <FormattedMessage id="shippingInvoiceAddressLabel" />
            ) : (
              <FormattedMessage id="shippingAddressLabel" />
            )}
          </SectionTitle>
          <div className="lh-copy f4-ns">{street}</div>
          <div className="lh-copy f4-ns">
            {neighborhood}
            {' - '}
            {city}
            {' - '}
            {state}
            <Link
              onClick={handleToggleAddressCollapsed}
              className="ml4 f4-ns f6 mr5-l"
              intlId="shippingAddressChangeLabel"
            />
          </div>
        </div>
      ) : (
        <div className="">
          {renderInput('street')}
          {renderInput('neighborhood')}
          {renderInput('city')}
          {renderInput('state')}
        </div>
      )}

      <div className="flex items-end">
        <div className="flex-none w3 w4-ns mr4 mr5-ns">
          {renderInput('number', isAddressCollapsed && !number, 'tel')}
        </div>
        <div className="flex-auto">{renderInput('complement')}</div>
      </div>
      {showAddressReference && (
        <div className="pt5">{renderInput('reference')}</div>
      )}
    </div>
  )
}

AddressForm.defaultProps = {
  allowCollapsed: true,
}

// @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'FC<AddressFormProps>' is not ass... Remove this comment to see the full error message
export default injectIntl(withMediaQuery(AddressForm))
